import { useState, useEffect, useCallback } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/utils/cn'

interface RedirectModalProps {
  isOpen: boolean
  onClose: () => void
  targetUrl: string
}

export function RedirectModal({
  isOpen,
  onClose,
  targetUrl,
}: RedirectModalProps) {
  const [countdown, setCountdown] = useState(4)

  const handleRedirect = useCallback(() => {
    window.location.href = targetUrl
  }, [targetUrl])

  // Reset countdown when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountdown(4)
    }
  }, [isOpen])

  // Countdown timer
  useEffect(() => {
    if (!isOpen) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          handleRedirect()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, handleRedirect])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div
        className={cn(
          'relative w-full max-w-sm mx-4 overflow-hidden',
          'rounded-2xl bg-[#0a0a0a] border border-white/[0.08]',
          'shadow-[0_0_0_1px_rgba(255,255,255,0.05),0_25px_50px_-12px_rgba(0,0,0,0.8)]',
          'animate-in zoom-in-95 slide-in-from-bottom-4 duration-300'
        )}
      >
        {/* Content */}
        <div className="p-6">
          {/* Animated icon */}
          <div className="flex justify-center mb-5">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-full bg-white/10 blur-xl animate-pulse" />
              {/* Icon container */}
              <div className="relative size-14 rounded-full bg-gradient-to-b from-white/10 to-white/5 border border-white/10 flex items-center justify-center">
                <ArrowUpRight className="size-6 text-white/80" strokeWidth={1.5} />
              </div>
            </div>
          </div>

          {/* Title & description */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-medium text-white mb-1.5">
              You are about to be redirected...
            </h2>
            <p className="text-sm text-white/40 leading-relaxed">
              Taking you to our main site at Pixelmaker.ai
            </p>
          </div>

          {/* Redirect button with progress */}
          <button
            onClick={handleRedirect}
            className={cn(
              'relative w-full py-3 px-4 rounded-xl text-sm font-medium overflow-hidden transition-all',
              'bg-white text-black',
              'hover:bg-white/90',
              'active:scale-[0.98]'
            )}
          >
            {/* Progress bar */}
            <div
              className="absolute inset-0 bg-black/10 origin-left transition-transform duration-1000 ease-linear"
              style={{ transform: `scaleX(${1 - countdown / 4})` }}
            />
            <span className="relative z-10 flex items-center justify-center gap-2">
              Continue ({countdown}s)
              <ArrowUpRight className="size-4" strokeWidth={2} />
            </span>
          </button>

          {/* Cancel link */}
          <button
            onClick={onClose}
            className="w-full mt-3 py-2 text-xs text-white/30 hover:text-white/50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

// Hook to manage redirect modal state
export function useRedirectModal() {
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    targetUrl: string
    title?: string
    description?: string
  }>({
    isOpen: false,
    targetUrl: '',
  })

  const openRedirect = useCallback((
    targetUrl: string, 
    options?: { title?: string; description?: string }
  ) => {
    setModalState({
      isOpen: true,
      targetUrl,
      title: options?.title,
      description: options?.description,
    })
  }, [])

  const closeRedirect = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
  }, [])

  return {
    modalState,
    openRedirect,
    closeRedirect,
  }
}
